// i18n fragment - populated by the Phase 6 sweep. See ../strings.ts.
// Covers web-ui modals + side panels: AddWorkspace / Invite / Prompt /
// Confirm / AvatarGenerate / SidePanel close + the integration panels
// (custom MCP add + policy, connectors, API-key connect, scope picker,
// document upload).
import type { LocaleStrings } from './base';

export const componentsStrings: LocaleStrings = {
  en: {
    // ── AddWorkspaceModal.vue ──
    'addWorkspace.title':            'Create a workspace',
    'addWorkspace.lede':             "A brand-new HQ workspace, owned by you. Add Slack or Teams whenever you're ready - you'll land in setup next.",
    'addWorkspace.name_label':       'Workspace name',
    'addWorkspace.name_placeholder': 'e.g. Acme',
    'addWorkspace.checking':         'Checking availability...',
    'addWorkspace.slug_free_before': 'Your workspace will live at',
    'addWorkspace.slug_taken_after': "is taken - we'll add a suffix to keep it unique.",
    'addWorkspace.invite_label':     'Invite code',
    'addWorkspace.invite_placeholder':'Invite code',
    'addWorkspace.invite_hint':      'Creating a workspace needs an invite during beta.',
    'addWorkspace.website_label':    'Company website',
    'addWorkspace.optional':         '(optional)',
    'addWorkspace.creating':         'Creating…',
    'addWorkspace.create':           'Create workspace',
    'addWorkspace.err.create':       'Could not create the workspace.',

    // ── InviteModal.vue ──
    'invite.title':                  'Invite people',
    'invite.title_named':            'Invite people to {name}',
    'invite.emails_label':           'Email addresses',
    'invite.emails_placeholder':     'name@company.com, paste a list…',
    'invite.emails_hint':            'Type or paste addresses - separate with commas, spaces, or new lines.',
    'invite.role_label':             'Role',
    'invite.role_member':            'Member',
    'invite.role_admin':             'Admin',
    'invite.role_admin_hint':        'Admins manage agents, integrations, settings, and people.',
    'invite.role_member_hint':       'Members chat and use installed agents and tools.',
    'invite.summary_one':            '{sent} invite sent.',
    'invite.summary_other':          '{sent} invites sent.',
    'invite.summary_with_failed_one':'{sent} invite sent, {failed} failed.',
    'invite.summary_with_failed_other':'{sent} invites sent, {failed} failed.',
    'invite.success_hint':           "They'll get a sign-in link by email to finish setting up.",
    'invite.sending':                'Sending…',
    'invite.send_one':               'Send invite',
    'invite.send_other':             'Send invites',
    'invite.failed':                 'failed',

    // ── PromptModal.vue (own literals only) ──
    'prompt.ok':                     'OK',

    // ── ConfirmModal.vue (own literals only) ──
    'confirm.action_error':          'Something went wrong.',
    'confirm.type_before':           'Type',
    'confirm.type_after':            'to confirm',

    // ── AvatarGenerateModal.vue ──
    'avatarGen.default_look':         'Warm, approachable, and professional. Calm, confident expression; soft muted colour palette.',
    'avatarGen.title':                'Generate an avatar',
    'avatarGen.describe_label':       'Describe the look',
    'avatarGen.describe_placeholder': 'e.g. calm and approachable, soft blue palette, subtle nod to finance',
    'avatarGen.hint':                 'We keep the house illustration style and safety guidelines - this just steers the subject and mood. Generation takes a few seconds.',
    'avatarGen.generating':           'Generating…',
    'avatarGen.generate':             'Generate',

    // ── AddCustomIntegrationPanel.vue ──
    'customIntegration.title':            'Add custom integration',
    'customIntegration.subtitle':         'Connect any MCP server by URL',
    'customIntegration.url_label':        'Server URL',
    'customIntegration.url_hint':         "Paste the MCP server's HTTPS endpoint. We detect how it authenticates (open, API key, or OAuth) and what tools it offers.",
    'customIntegration.detecting':        'Detecting…',
    'customIntegration.detect':           'Detect',
    'customIntegration.server_fallback':  'MCP server',
    'customIntegration.not_connectable':  "This server can't be connected via self-serve setup.",
    'customIntegration.summary_oauth':    "You'll be redirected to the provider to authorize your workspace.",
    'customIntegration.summary_api_key':  "You'll be asked for an API key to finish connecting.",
    'customIntegration.summary_client_credentials': 'This server uses machine-to-machine credentials. You will paste an API client id and secret next - there is no sign-in step.',
    'customIntegration.summary_open_one': 'Open server - {count} tool available, no sign-in needed.',
    'customIntegration.summary_open_other':'Open server - {count} tools available, no sign-in needed.',
    'customIntegration.tools_label':      'Tools',
    'customIntegration.tools_more':       '+{count} more',
    'customIntegration.name_placeholder': 'Custom integration',
    'customIntegration.name_hint':        'Shown in the integrations list and to your agents.',
    'customIntegration.limit_label':      'Daily call limit',
    'customIntegration.optional':         'optional',
    'customIntegration.limit_placeholder':'No limit',
    'customIntegration.limit_hint':       'The most calls your agents can make to this integration in a 24h window, shared across every agent. Past the cap, further calls are blocked until the window resets. Blank = no limit.',
    'customIntegration.adding':           'Adding…',
    'customIntegration.connect':          'Connect',
    'customIntegration.continue':         'Continue',
    'customIntegration.add':              'Add',
    'customIntegration.err.unreachable':  'Could not reach that server.',
    'customIntegration.err.add':          'Could not add that integration.',

    // ── CustomIntegrationPolicyPanel.vue ──
    'customPolicy.title':            'Custom integration policy',
    'customPolicy.subtitle':         'Govern what your workspace can connect',
    'customPolicy.allow_title':      'Allow custom integrations',
    'customPolicy.allow_hint':       'When off, no one can add an MCP server by URL in this workspace.',
    'customPolicy.approval_title':   'Require approval',
    'customPolicy.approval_hint':    'A newly added integration stays disabled until an admin approves it. Changed tool sets are always re-frozen for review.',
    'customPolicy.approval_aria':    'Require approval before agents can use a new integration',
    'customPolicy.allowed_label':    'Allowed domains',
    'customPolicy.allowed_hint':     'Comma-separated. Leave blank to allow any host. Subdomains match (acme.com covers mcp.acme.com).',
    'customPolicy.blocked_label':    'Blocked domains',
    'customPolicy.blocked_hint':     'Always refused, even if allowed above.',
    'customPolicy.save':             'Save policy',
    'customPolicy.saved_toast':      'Integration policy saved.',
    'customPolicy.err.load':         'Failed to load policy',
    'customPolicy.err.save':         'Failed to save policy',

    // ── ConnectorsPanel.vue ──
    'connectors.title':              'Private connectors',
    'connectors.subtitle':           'Reach MCP servers on your own network',
    'connectors.lede':               'A connector is a small agent you run inside your network. It connects out to HQ over mTLS (no inbound ports) and lets your agents reach a private MCP server.',
    'connectors.name_placeholder':   'Connector name (e.g. Acme datacenter)',
    'connectors.creating':           'Creating…',
    'connectors.new':                'New connector',
    'connectors.shown_once':         'Shown once',
    'connectors.enroll_hint':        'Run this where the connector will live. The token is one-time and expires in 1 hour.',
    'connectors.status_online':      'online',
    'connectors.status_enrolled':    'enrolled',
    'connectors.status_pending':     'pending',
    'connectors.status_revoked':     'revoked',
    'connectors.revoke_tip':         'Revoke connector',
    'connectors.mcp_name_placeholder':'Private MCP name',
    'connectors.add_mcp':            'Add MCP',
    'connectors.pending_hint':       'Waiting for the connector to enroll - run the command above.',
    'connectors.empty':              'No connectors yet.',
    'connectors.revoke_title':       'Revoke {name}?',
    'connectors.revoke_body':        'The connector can no longer reconnect. Private MCPs behind it stop working. You can add a new connector later.',
    'connectors.revoke':             'Revoke',
    'connectors.revoked_toast':      '{name} revoked.',
    'connectors.mcp_added_toast':    "{name} added. It'll appear in Integrations.",
    'connectors.copied_toast':       'Enrollment command copied.',
    'connectors.copy_failed_toast':  'Could not copy - select the text manually.',
    'connectors.err.load':           'Failed to load connectors',
    'connectors.err.create':         'Failed to create connector',
    'connectors.err.revoke':         'Failed to revoke',
    'connectors.err.add_mcp':        'Failed to add private MCP',
    'connectors.download_title':     'Get the connector agent',
    'connectors.download_hint':      'Download the agent for the machine that will run it, then create a connector below and run the one-time enrollment command on that machine.',
    'connectors.download':           'Download',
    'connectors.download_all':       'All builds + checksums',
    'connectors.setup_guide':        'Setup guide',
    'connectors.arch_x86_64':        'Intel / x64',
    'connectors.arch_aarch64':       'ARM64',
    'connectors.run_hint_linux':     'Linux: chmod +x the file, then run the enroll command.',
    'connectors.run_hint_macos':     'macOS: chmod +x the file. If Gatekeeper blocks it, run: xattr -d com.apple.quarantine <file>',
    'connectors.run_hint_windows':   'Windows: run it from PowerShell. SmartScreen may warn on first launch (the binary is unsigned).',

    // ── ApiKeyConnectPanel.vue ──
    'apiKey.rotating':               'Rotating…',
    'apiKey.connecting':             'Connecting…',
    'apiKey.rotate':                 'Rotate key',
    'apiKey.connect':                'Connect',
    'apiKey.hint_stripe':            'Stripe restricted key (rk_test_… or rk_live_…). Test mode is recommended for first connect - restricted to read scopes.',
    'apiKey.hint_linear':            'Linear personal API key from Settings > API.',
    'apiKey.hint_notion':            'Notion internal integration token (secret_…).',
    'apiKey.hint_openai':            'OpenAI API key (sk-…). Will be used by the agent on behalf of this workspace.',
    'apiKey.hint_generic':           "Paste the provider's API token. The key is sealed with workspace-scoped encryption before it touches storage - it can't be read back at rest.",
    'apiKey.title_rotate':           'Rotate API key',
    'apiKey.title_connect':          'Connect with API key',
    'apiKey.key_label':              'API key',
    'apiKey.key_placeholder':        'paste here',
    'apiKey.err.rotate':             'Failed to rotate key',
    'apiKey.err.connect':            'Failed to connect',

    // ── ClientCredentialsConnectPanel.vue ──
    'clientCreds.title_connect':     'Connect with API client',
    'clientCreds.title_rotate':      'Rotate API client',
    'clientCreds.intro':             'This server uses machine-to-machine credentials. Create an API client with your provider and paste its id and secret here - there is no sign-in step.',
    'clientCreds.id_label':          'Client ID',
    'clientCreds.id_placeholder':    'Client ID',
    'clientCreds.secret_label':      'Client secret',
    'clientCreds.secret_placeholder':'Client secret',
    'clientCreds.secret_hint':       'The secret is sealed with workspace-scoped encryption before it touches storage, and is used only to renew the access token when it expires.',
    'clientCreds.connect':           'Connect',
    'clientCreds.rotate':            'Rotate credentials',
    'clientCreds.connecting':        'Connecting...',
    'clientCreds.rotating':          'Rotating...',
    'clientCreds.err.connect':       "Couldn't connect with those credentials.",
    'clientCreds.err.rotate':        "Couldn't rotate those credentials.",

    // ── ScopePicker.vue ──
    'scope.admin_only':              '(admin only)',

    // ── ScopePickerPanel.vue ──
    'scopePicker.title':             'Connect',
    'scopePicker.title_named':       'Connect {name}',
    'scopePicker.lede':              'Some integrations work either as a shared workspace credential or as a per-user connection. Pick how you want this one to land.',
    'scopePicker.team_title':        'For the whole workspace',
    'scopePicker.team_hint':         'One shared credential used for every agent call, regardless of who triggered it. Right choice for company-wide systems (Stripe, GitHub org, shared CRM).',
    'scopePicker.user_title':        'Just me',
    'scopePicker.user_hint':         "Pinned to your account. Calls you trigger use your credential; other people's calls fall back to the workspace credential if one exists. Right for personal-data integrations (your Gmail / Calendar / Drive).",

    // ── DocumentUploadPanel.vue ──
    'docUpload.title':               'Upload document',
    'docUpload.lede':                'Save a file to your library. Private by default; share with the team if it should be workspace-wide.',
    'docUpload.file_label':          'File',
    'docUpload.drop_aria':           'Choose file or drop one here',
    'docUpload.drop_strong':         'Drop a file here',
    'docUpload.drop_or':             'or',
    'docUpload.drop_link':           'choose one',
    'docUpload.unknown_type':        'unknown type',
    'docUpload.visibility_label':    'Visibility',
    'docUpload.private':             'Private',
    'docUpload.private_hint':        'only you',
    'docUpload.team':                'Team',
    'docUpload.team_hint':           'everyone in this workspace',
    'docUpload.category_label':      'Category',
    'docUpload.optional':            '(optional)',
    'docUpload.category_placeholder':'Reports / Contracts / Research',
    'docUpload.tags_label':          'Tags',
    'docUpload.tags_hint':           'comma-separated',
    'docUpload.caption_label':       'Caption',
    'docUpload.caption_hint':        '(one line)',
    'docUpload.caption_placeholder': 'What this file is - short reminder for future-you.',
    'docUpload.too_large':           'File is too large ({size} MB). Limit is {limit} MB for inline upload.',
    'docUpload.err.upload':          'Upload failed',
    'docUpload.uploading':           'Uploading...',
    'docUpload.upload':              'Upload',
  },
  sv: {
    // ── AddWorkspaceModal.vue ──
    'addWorkspace.title':            'Skapa en arbetsyta',
    'addWorkspace.lede':             'En helt ny HQ-arbetsyta som du äger. Lägg till Slack eller Teams när du är redo - du landar i uppsättningen härnäst.',
    'addWorkspace.name_label':       'Arbetsytans namn',
    'addWorkspace.name_placeholder': 't.ex. Acme',
    'addWorkspace.checking':         'Kontrollerar tillgänglighet...',
    'addWorkspace.slug_free_before': 'Din arbetsyta kommer att ligga på',
    'addWorkspace.slug_taken_after': 'är upptaget - vi lägger till ett suffix för att hålla det unikt.',
    'addWorkspace.invite_label':     'Inbjudningskod',
    'addWorkspace.invite_placeholder':'Inbjudningskod',
    'addWorkspace.invite_hint':      'Att skapa en arbetsyta kräver en inbjudan under betan.',
    'addWorkspace.website_label':    'Företagets webbplats',
    'addWorkspace.optional':         '(valfritt)',
    'addWorkspace.creating':         'Skapar…',
    'addWorkspace.create':           'Skapa arbetsyta',
    'addWorkspace.err.create':       'Det gick inte att skapa arbetsytan.',

    // ── InviteModal.vue ──
    'invite.title':                  'Bjud in personer',
    'invite.title_named':            'Bjud in personer till {name}',
    'invite.emails_label':           'E-postadresser',
    'invite.emails_placeholder':     'namn@foretag.com, klistra in en lista…',
    'invite.emails_hint':            'Skriv eller klistra in adresser - separera med komma, mellanslag eller nya rader.',
    'invite.role_label':             'Roll',
    'invite.role_member':            'Medlem',
    'invite.role_admin':             'Administratör',
    'invite.role_admin_hint':        'Administratörer hanterar agenter, integrationer, inställningar och personer.',
    'invite.role_member_hint':       'Medlemmar chattar och använder installerade agenter och verktyg.',
    'invite.summary_one':            '{sent} inbjudan skickad.',
    'invite.summary_other':          '{sent} inbjudningar skickade.',
    'invite.summary_with_failed_one':'{sent} inbjudan skickad, {failed} misslyckades.',
    'invite.summary_with_failed_other':'{sent} inbjudningar skickade, {failed} misslyckades.',
    'invite.success_hint':           'De får en inloggningslänk via e-post för att slutföra uppsättningen.',
    'invite.sending':                'Skickar…',
    'invite.send_one':               'Skicka inbjudan',
    'invite.send_other':             'Skicka inbjudningar',
    'invite.failed':                 'misslyckades',

    // ── PromptModal.vue (own literals only) ──
    'prompt.ok':                     'OK',

    // ── ConfirmModal.vue (own literals only) ──
    'confirm.action_error':          'Något gick fel.',
    'confirm.type_before':           'Skriv',
    'confirm.type_after':            'för att bekräfta',

    // ── AvatarGenerateModal.vue ──
    'avatarGen.default_look':         'Varm, tillmötesgående och professionell. Lugnt, självsäkert uttryck; mjuk, dämpad färgpalett.',
    'avatarGen.title':                'Generera en avatar',
    'avatarGen.describe_label':       'Beskriv utseendet',
    'avatarGen.describe_placeholder': 't.ex. lugn och tillmötesgående, mjuk blå palett, en subtil koppling till finans',
    'avatarGen.hint':                 'Vi behåller husets illustrationsstil och säkerhetsriktlinjer - detta styr bara motivet och stämningen. Genereringen tar några sekunder.',
    'avatarGen.generating':           'Genererar…',
    'avatarGen.generate':             'Generera',

    // ── AddCustomIntegrationPanel.vue ──
    'customIntegration.title':            'Lägg till anpassad integration',
    'customIntegration.subtitle':         'Anslut valfri MCP-server via URL',
    'customIntegration.url_label':        'Server-URL',
    'customIntegration.url_hint':         'Klistra in MCP-serverns HTTPS-slutpunkt. Vi upptäcker hur den autentiserar (öppen, API-nyckel eller OAuth) och vilka verktyg den erbjuder.',
    'customIntegration.detecting':        'Upptäcker…',
    'customIntegration.detect':           'Upptäck',
    'customIntegration.server_fallback':  'MCP-server',
    'customIntegration.not_connectable':  'Den här servern kan inte anslutas via självbetjäningsuppsättning.',
    'customIntegration.summary_oauth':    'Du omdirigeras till leverantören för att auktorisera din arbetsyta.',
    'customIntegration.summary_api_key':  'Du blir ombedd att ange en API-nyckel för att slutföra anslutningen.',
    'customIntegration.summary_client_credentials': 'Den här servern använder maskin-till-maskin-uppgifter. Du klistrar in ett API-klient-ID och en hemlighet härnäst - det finns inget inloggningssteg.',
    'customIntegration.summary_open_one': 'Öppen server - {count} verktyg tillgängligt, ingen inloggning behövs.',
    'customIntegration.summary_open_other':'Öppen server - {count} verktyg tillgängliga, ingen inloggning behövs.',
    'customIntegration.tools_label':      'Verktyg',
    'customIntegration.tools_more':       '+{count} till',
    'customIntegration.name_placeholder': 'Anpassad integration',
    'customIntegration.name_hint':        'Visas i integrationslistan och för dina agenter.',
    'customIntegration.limit_label':      'Daglig anropsgräns',
    'customIntegration.optional':         'valfritt',
    'customIntegration.limit_placeholder':'Ingen gräns',
    'customIntegration.limit_hint':       'Begränsa hur många gånger om dagen dina agenter kan anropa den här integrationen. Tom = ingen gräns.',
    'customIntegration.adding':           'Lägger till…',
    'customIntegration.connect':          'Anslut',
    'customIntegration.continue':         'Fortsätt',
    'customIntegration.add':              'Lägg till',
    'customIntegration.err.unreachable':  'Det gick inte att nå den servern.',
    'customIntegration.err.add':          'Det gick inte att lägga till den integrationen.',

    // ── CustomIntegrationPolicyPanel.vue ──
    'customPolicy.title':            'Policy för anpassade integrationer',
    'customPolicy.subtitle':         'Styr vad din arbetsyta får ansluta',
    'customPolicy.allow_title':      'Tillåt anpassade integrationer',
    'customPolicy.allow_hint':       'När detta är av kan ingen lägga till en MCP-server via URL i den här arbetsytan.',
    'customPolicy.approval_title':   'Kräv godkännande',
    'customPolicy.approval_hint':    'En nyligen tillagd integration förblir inaktiverad tills en administratör godkänner den. Ändrade verktygsuppsättningar fryses alltid på nytt för granskning.',
    'customPolicy.approval_aria':    'Kräv godkännande innan agenter kan använda en ny integration',
    'customPolicy.allowed_label':    'Tillåtna domäner',
    'customPolicy.allowed_hint':     'Kommaseparerade. Lämna tomt för att tillåta valfri värd. Underdomäner matchar (acme.com täcker mcp.acme.com).',
    'customPolicy.blocked_label':    'Blockerade domäner',
    'customPolicy.blocked_hint':     'Avvisas alltid, även om de tillåts ovan.',
    'customPolicy.save':             'Spara policy',
    'customPolicy.saved_toast':      'Integrationspolicyn sparades.',
    'customPolicy.err.load':         'Det gick inte att läsa in policyn',
    'customPolicy.err.save':         'Det gick inte att spara policyn',

    // ── ConnectorsPanel.vue ──
    'connectors.title':              'Privata anslutare',
    'connectors.subtitle':           'Nå MCP-servrar på ditt eget nätverk',
    'connectors.lede':               'En anslutare är en liten agent som du kör inuti ditt nätverk. Den ansluter till HQ via mTLS (inga inkommande portar) och låter dina agenter nå en privat MCP-server.',
    'connectors.name_placeholder':   'Anslutarnamn (t.ex. Acme datacenter)',
    'connectors.creating':           'Skapar…',
    'connectors.new':                'Ny anslutare',
    'connectors.shown_once':         'Visas en gång',
    'connectors.enroll_hint':        'Kör detta där anslutaren ska bo. Token är engångs och går ut om 1 timme.',
    'connectors.status_online':      'online',
    'connectors.status_enrolled':    'registrerad',
    'connectors.status_pending':     'väntar',
    'connectors.status_revoked':     'återkallad',
    'connectors.revoke_tip':         'Återkalla anslutare',
    'connectors.mcp_name_placeholder':'Privat MCP-namn',
    'connectors.add_mcp':            'Lägg till MCP',
    'connectors.pending_hint':       'Väntar på att anslutaren ska registreras - kör kommandot ovan.',
    'connectors.empty':              'Inga anslutare än.',
    'connectors.revoke_title':       'Återkalla {name}?',
    'connectors.revoke_body':        'Anslutaren kan inte längre återansluta. Privata MCP:er bakom den slutar fungera. Du kan lägga till en ny anslutare senare.',
    'connectors.revoke':             'Återkalla',
    'connectors.revoked_toast':      '{name} återkallad.',
    'connectors.mcp_added_toast':    '{name} tillagd. Den visas i Integrationer.',
    'connectors.copied_toast':       'Registreringskommandot kopierat.',
    'connectors.copy_failed_toast':  'Det gick inte att kopiera - markera texten manuellt.',
    'connectors.err.load':           'Det gick inte att läsa in anslutare',
    'connectors.err.create':         'Det gick inte att skapa anslutaren',
    'connectors.err.revoke':         'Det gick inte att återkalla',
    'connectors.err.add_mcp':        'Det gick inte att lägga till privat MCP',
    'connectors.download_title':     'Hämta anslutaragenten',
    'connectors.download_hint':      'Ladda ner agenten för maskinen som ska köra den, skapa sedan en anslutare nedan och kör engångskommandot för registrering på den maskinen.',
    'connectors.download':           'Ladda ner',
    'connectors.download_all':       'Alla byggen + kontrollsummor',
    'connectors.setup_guide':        'Installationsguide',
    'connectors.arch_x86_64':        'Intel / x64',
    'connectors.arch_aarch64':       'ARM64',
    'connectors.run_hint_linux':     'Linux: kör chmod +x på filen, kör sedan enroll-kommandot.',
    'connectors.run_hint_macos':     'macOS: kör chmod +x på filen. Om Gatekeeper blockerar den: xattr -d com.apple.quarantine <fil>',
    'connectors.run_hint_windows':   'Windows: kör den från PowerShell. SmartScreen kan varna vid första start (binären är osignerad).',

    // ── ApiKeyConnectPanel.vue ──
    'apiKey.rotating':               'Roterar…',
    'apiKey.connecting':             'Ansluter…',
    'apiKey.rotate':                 'Rotera nyckel',
    'apiKey.connect':                'Anslut',
    'apiKey.hint_stripe':            'Stripe-begränsad nyckel (rk_test_… eller rk_live_…). Testläge rekommenderas vid första anslutningen - begränsad till läsbehörigheter.',
    'apiKey.hint_linear':            'Linear personlig API-nyckel från Inställningar > API.',
    'apiKey.hint_notion':            'Notion intern integrationstoken (secret_…).',
    'apiKey.hint_openai':            'OpenAI API-nyckel (sk-…). Används av agenten å den här arbetsytans vägnar.',
    'apiKey.hint_generic':           'Klistra in leverantörens API-token. Nyckeln förseglas med arbetsytespecifik kryptering innan den når lagring - den kan inte läsas tillbaka i vila.',
    'apiKey.title_rotate':           'Rotera API-nyckel',
    'apiKey.title_connect':          'Anslut med API-nyckel',
    'apiKey.key_label':              'API-nyckel',
    'apiKey.key_placeholder':        'klistra in här',
    'apiKey.err.rotate':             'Det gick inte att rotera nyckeln',
    'apiKey.err.connect':            'Det gick inte att ansluta',

    // ── ClientCredentialsConnectPanel.vue ──
    'clientCreds.title_connect':     'Anslut med API-klient',
    'clientCreds.title_rotate':      'Rotera API-klient',
    'clientCreds.intro':             'Den här servern använder maskin-till-maskin-uppgifter. Skapa en API-klient hos leverantören och klistra in dess id och hemlighet här - det finns inget inloggningssteg.',
    'clientCreds.id_label':          'Klient-ID',
    'clientCreds.id_placeholder':    'Klient-ID',
    'clientCreds.secret_label':      'Klienthemlighet',
    'clientCreds.secret_placeholder':'Klienthemlighet',
    'clientCreds.secret_hint':       'Hemligheten förseglas med arbetsytans kryptering innan den lagras, och används endast för att förnya åtkomsttoken när den går ut.',
    'clientCreds.connect':           'Anslut',
    'clientCreds.rotate':            'Rotera uppgifter',
    'clientCreds.connecting':        'Ansluter...',
    'clientCreds.rotating':          'Roterar...',
    'clientCreds.err.connect':       'Det gick inte att ansluta med de uppgifterna.',
    'clientCreds.err.rotate':        'Det gick inte att rotera de uppgifterna.',

    // ── ScopePicker.vue ──
    'scope.admin_only':              '(endast administratör)',

    // ── ScopePickerPanel.vue ──
    'scopePicker.title':             'Anslut',
    'scopePicker.title_named':       'Anslut {name}',
    'scopePicker.lede':              'Vissa integrationer fungerar antingen som en delad arbetsyteinloggning eller som en anslutning per användare. Välj hur du vill att den här ska landa.',
    'scopePicker.team_title':        'För hela arbetsytan',
    'scopePicker.team_hint':         'En delad inloggning som används för varje agentanrop, oavsett vem som utlöste det. Rätt val för företagsövergripande system (Stripe, GitHub-org, delat CRM).',
    'scopePicker.user_title':        'Bara jag',
    'scopePicker.user_hint':         'Knuten till ditt konto. Anrop du utlöser använder din inloggning; andras anrop faller tillbaka på arbetsytans inloggning om en finns. Rätt för integrationer med personuppgifter (din Gmail / kalender / Drive).',

    // ── DocumentUploadPanel.vue ──
    'docUpload.title':               'Ladda upp dokument',
    'docUpload.lede':                'Spara en fil i ditt bibliotek. Privat som standard; dela med teamet om den ska vara arbetsyteomfattande.',
    'docUpload.file_label':          'Fil',
    'docUpload.drop_aria':           'Välj fil eller släpp en här',
    'docUpload.drop_strong':         'Släpp en fil här',
    'docUpload.drop_or':             'eller',
    'docUpload.drop_link':           'välj en',
    'docUpload.unknown_type':        'okänd typ',
    'docUpload.visibility_label':    'Synlighet',
    'docUpload.private':             'Privat',
    'docUpload.private_hint':        'endast du',
    'docUpload.team':                'Team',
    'docUpload.team_hint':           'alla i den här arbetsytan',
    'docUpload.category_label':      'Kategori',
    'docUpload.optional':            '(valfritt)',
    'docUpload.category_placeholder':'Rapporter / Avtal / Research',
    'docUpload.tags_label':          'Taggar',
    'docUpload.tags_hint':           'kommaseparerade',
    'docUpload.caption_label':       'Bildtext',
    'docUpload.caption_hint':        '(en rad)',
    'docUpload.caption_placeholder': 'Vad den här filen är - en kort påminnelse till framtida dig.',
    'docUpload.too_large':           'Filen är för stor ({size} MB). Gränsen är {limit} MB för inline-uppladdning.',
    'docUpload.err.upload':          'Uppladdningen misslyckades',
    'docUpload.uploading':           'Laddar upp...',
    'docUpload.upload':              'Ladda upp',
  },
};
